import {
  availableSupProfileHours,
  type AvailableSupProfileSessionHour,
  type NativeWindowScore,
  type SupProfileSessionHour,
} from "./api-types";
import type {
  FitAvailability,
  NearbySuggestion,
  SessionHour,
  SessionPlan,
  SessionPreferences,
  SessionWindow,
} from "./types";
import {
  requiredSupPersonalFitEvidence,
  type SessionContext,
} from "./comfort";
import { buildWindows } from "./windows";
import { buildSailingWindows } from "./sailing";
import { describeWindow, redSafetyExclusionReason, waterComfortFor } from "./explain";
import { comprehensionFor, personalFitContext, prefAbility } from "./preferences";
import { windowWarnings } from "./warnings";
import type { ConditionSafetyState } from "../conditions/safety-notes";
import {
  sportPresentation,
  type SupProfileConfidence,
} from "../conditions/sport-profiles";

const SPORT_LABEL: Record<string, string> = {
  surf: "surf",
  bodyboarding: "bodyboarding",
  wind: "wind",
  foil: "foil",
  sup: "SUP",
  swim: "swimming",
  diving: "diving",
  sailing: "sailing",
};

export function sportLabel(sport: string): string {
  return SPORT_LABEL[sport] ?? sport;
}

export type BuildPlanInput<THour extends SessionHour = SessionHour> = {
  prefs: SessionPreferences;
  hours: readonly THour[];
  ctx: SessionContext;
  waterTempC: number | null;
  spotName: string;
  nearby?: NearbySuggestion | null;
  nativeWindows?: NativeWindowScore[];
};

/**
 * Stored safety remains authoritative. When a Wind user has selected a
 * discipline, specialise only the wording of an already-carried canonical
 * gust warning; never create a new veto from client-side weather.
 */
function contextualiseWindSafety<THour extends SessionHour>(
  hours: readonly THour[],
  prefs: SessionPreferences,
): THour[] {
  const fit = personalFitContext(prefs);
  if (fit?.kind !== "wind" || fit.mode !== "kitesurf") return [...hours];

  return hours.map((hour) => {
    if (
      hour.safetyState !== "flagged-red" ||
      !hour.safetyNotes?.includes("Severe gust volatility")
    ) return hour;
    return {
      ...hour,
      safetyNotes: hour.safetyNotes.map((note) =>
        note === "Severe gust volatility" ? "Lofting gusts" : note,
      ),
    } as THour;
  });
}

export function buildPlan<THour extends SessionHour>(
  input: BuildPlanInput<THour>,
): SessionPlan<SessionWindow<THour>> {
  const { prefs, ctx, waterTempC, spotName } = input;
  const hours = contextualiseWindSafety(input.hours, prefs);
  const ability = prefAbility(prefs);
  const comprehension = comprehensionFor(ability);

  const raw =
    prefs.sport === "sailing" && input.nativeWindows?.length
      ? buildSailingWindows(hours, prefs, ctx, input.nativeWindows)
      : buildWindows(hours, prefs, ctx);
  const redHour = hours.find((hour) => hour.safetyState === "flagged-red");
  const fallbackRedHour = ctx.safetyState === "flagged-red"
    ? hours.find((hour) => hour.dayOffset === 0 && (hour.safetyState === undefined || hour.safetyState === "no-data"))
    : undefined;
  const redNative = input.nativeWindows?.find((window) => window.safetyState === "flagged-red");
  const excludedWarning = redHour
    ? `${redHour.dayOffset === 0 ? "Today" : "Tomorrow"} ${String(redHour.hour).padStart(2, "0")}:00 excluded by canonical safety${redHour.safetyNotes?.[0] ? `: ${redHour.safetyNotes[0]}` : ""}`
    : redNative
      ? `${redNative.dayOffset === 0 ? "Today" : "Tomorrow"} ${String(redNative.startHour).padStart(2, "0")}:00-${String(redNative.endHour).padStart(2, "0")}:00 excluded by canonical safety${redNative.safetyNotes?.[0] ? `: ${redNative.safetyNotes[0]}` : ""}`
      : fallbackRedHour
        ? `Today ${String(fallbackRedHour.hour).padStart(2, "0")}:00 excluded by canonical safety${ctx.safetyNotes?.[0] ? `: ${ctx.safetyNotes[0]}` : ""}`
      : null;
  const windows = raw.map((w) => {
    const { headline, explanation } = describeWindow(w, prefs, comprehension, ctx);
    const warnings = [...(excludedWarning ? [excludedWarning] : []), ...windowWarnings(w, ctx)]
      .filter((warning, index, all) => all.indexOf(warning) === index)
      .slice(0, 3);
    return { ...w, headline, explanation, warnings };
  });

  const bestWindow = windows.find((w) => w.label === "best") ?? null;
  const hasGoodWindows = windows.some((w) => w.label === "best" || w.label === "good");

  const sportLabel = SPORT_LABEL[prefs.sport] ?? prefs.sport;
  const safetyExclusion = !hasGoodWindows
    ? redSafetyExclusionReason(ctx, hours) ?? (redNative
      ? `No personal window is shown because the canonical safety assessment is red${redNative.safetyNotes?.[0] ? `: ${redNative.safetyNotes[0]}.` : "."}`
      : null)
    : null;
  const allDayVerdict = safetyExclusion
    ? safetyExclusion
    : hasGoodWindows
    ? null
    : `${spotName} is not looking great for ${ability} ${sportLabel} over the next couple of days. ${
        input.nearby
          ? "There is a better option nearby, see below."
          : "Check back as the forecast updates."
      }`;

  return {
    sport: prefs.sport,
    ability,
    comprehension,
    windows,
    bestWindow,
    hasGoodWindows,
    allDayVerdict,
    waterComfort: waterComfortFor(prefs, waterTempC),
    nearbySuggestion: input.nearby ?? null,
  };
}

export type SupProfileSessionWindow =
  SessionWindow<AvailableSupProfileSessionHour> & {
    confidence: Exclude<SupProfileConfidence, "unavailable">;
    evidenceNote: "Partial forecast evidence" | null;
    safetyState: ConditionSafetyState;
  };

export type SupProfileBuildPlanInput = Omit<
  BuildPlanInput<AvailableSupProfileSessionHour>,
  "hours"
> & {
  hours: readonly SupProfileSessionHour[];
};

export type SupProfileSessionPlan = Omit<
  SessionPlan<SupProfileSessionWindow>,
  "windows" | "bestWindow"
> & {
  windows: SupProfileSessionWindow[];
  bestWindow: SupProfileSessionWindow | null;
  fitAvailability: FitAvailability;
  fitEvidenceNote: string | null;
};

function emptySupProfilePlan(
  input: SupProfileBuildPlanInput,
  fitEvidenceNote: string,
): SupProfileSessionPlan {
  const ability = prefAbility(input.prefs);
  return {
    sport: "sup",
    ability,
    comprehension: comprehensionFor(ability),
    windows: [],
    bestWindow: null,
    hasGoodWindows: false,
    allDayVerdict: null,
    waterComfort: waterComfortFor(input.prefs, input.waterTempC),
    nearbySuggestion: input.nearby ?? null,
    fitAvailability: "unavailable",
    fitEvidenceNote,
  };
}

function supWindowSafetyState(
  hours: readonly AvailableSupProfileSessionHour[],
): ConditionSafetyState {
  if (hours.some((hour) => hour.safetyState === "flagged-amber")) {
    return "flagged-amber";
  }
  if (hours.some((hour) => !hour.safetyState || hour.safetyState === "no-data")) {
    return "no-data";
  }
  return "clear";
}

export function buildSupProfilePlan(
  input: SupProfileBuildPlanInput,
): SupProfileSessionPlan {
  const fitContext = personalFitContext(input.prefs);
  if (fitContext?.kind !== "sup") {
    return emptySupProfilePlan(
      input,
      "Personal suitability unavailable: named SUP profile not confirmed",
    );
  }

  const missingFit = requiredSupPersonalFitEvidence(fitContext);
  if (missingFit) {
    return emptySupProfilePlan(
      input,
      missingFit.excludedBy ?? "Personal suitability unavailable",
    );
  }

  const numericHours = availableSupProfileHours(input.hours);
  const base = buildPlan({ ...input, hours: numericHours });
  const windows: SupProfileSessionWindow[] = base.windows.map((window) => {
    const confidence = window.hours.some((hour) => hour.confidence === "partial")
      ? "partial" as const
      : "full" as const;
    const hasUnavailableSafety = window.hours.some(
      (hour) => !hour.safetyState || hour.safetyState === "no-data",
    );
    const unavailableWarning = "Safety evidence unavailable for one or more hours";
    const warnings = [
      ...(hasUnavailableSafety ? [unavailableWarning] : []),
      ...window.warnings,
    ].filter((warning, index, all) => all.indexOf(warning) === index).slice(0, 3);
    return {
      ...window,
      warnings,
      confidence,
      evidenceNote: confidence === "partial" ? "Partial forecast evidence" : null,
      safetyState: supWindowSafetyState(window.hours),
    };
  });
  const bestWindow = windows.find((window) => window.label === "best") ?? null;
  const allDayVerdict = base.allDayVerdict &&
    !base.hasGoodWindows &&
    !base.allDayVerdict.startsWith(
      "No personal window is shown because the canonical safety assessment is red",
    )
    ? `${input.spotName} has no standout ${sportPresentation(fitContext.profile).label} profile window over the next couple of days. ${
        input.nearby
          ? "There is a better option nearby, see below."
          : "Check back as the forecast updates."
      }`
    : base.allDayVerdict;

  return {
    ...base,
    windows,
    bestWindow,
    allDayVerdict,
    fitAvailability: "available",
    fitEvidenceNote: null,
  };
}
